import { Draggable, getDroppablePayload } from "@msuite/ui/dnd";
import type { schema } from "@msuite/zero";
import type { Row } from "@rocicorp/zero";
import { Fragment } from "react";
import {
	ProjectCardTicketId,
	type SyncedTicket,
} from "@/components/cards/project-card/project-card-ticket-id";
import { useAuth } from "@/hooks/use-auth";
import {
	type TicketAssignmentType,
	type TicketType,
	useCrewHandler,
} from "@/sites/plan/plan-kanban/use-crew-handler";
import { useDailyContext } from "../daily/context";
import { useCrewContext } from "./context";
import { TicketAssignmentActionButton } from "./ticket-assignment-action-button";

/** Props Interface */
interface TicketAssignmentProps {
	// The relations ride along from the board queries; a guest board renders from them because
	// the detail endpoints refuse a foreign caller.
	assignment: TicketAssignmentType & {
		ticket?: SyncedTicket | null;
		project?: Row<typeof schema.tables.projects> | null;
	};
}

export function TicketAssignment({ assignment }: TicketAssignmentProps) {
	/** Context */
	const { canArrangeTicketOf, date, plan } = useDailyContext();
	const { crewId, planId } = useCrewContext();
	const crewHandler = useCrewHandler({ planId, crewId });
	const { organization } = useAuth();

	/** Computed */
	// On a guest board the tickets belong to the host, and the tickets endpoint refuses them to
	// a foreign caller — so the detail request is only started on the caller's own board.
	const isOwnBoard = plan?.orgId === organization?.id;
	// Dragging, reordering and removing this row is arranging the ticket's project — the fixed
	// rule, per assignment, not the board's `update Plan`.
	const canArrange = canArrangeTicketOf(assignment.projectId);

	/** Guard */
	if (!assignment) return null;

	/** Render */
	return (
		<Draggable
			accept={["ticket", "ticket-assignment"]}
			type="ticket-assignment"
			item={assignment}
			allowedEdges={["top", "bottom"]}
			className="py-1 ticket-assignment"
			handleOnDrop={(element) => {
				const edge = crewHandler.getClosestEdge(element);
				const payload = getDroppablePayload(element);
				if (payload.type === "ticket") {
					crewHandler.onDropTicket(payload.item as TicketType, {
						edge,
						assignmentId: assignment.id,
					});
				} else if (payload.type === "ticket-assignment") {
					crewHandler.onDropTicketAssignment(payload.item as TicketAssignmentType, {
						edge,
						assignmentId: assignment.id,
					});
				}
			}}
			isDisabled={!canArrange}
			id={`assignment-${assignment.id}`}
			data-assignment-id={assignment.id}
			data-assignment-ranking={assignment.ranking}
			data-target-date={date}
			data-ticket-id={assignment.ticketId}
		>
			<ProjectCardTicketId
				projectId={assignment.projectId}
				ticketId={assignment.ticketId}
				ticket={isOwnBoard ? undefined : (assignment.ticket ?? undefined)}
				project={isOwnBoard ? undefined : (assignment.project ?? undefined)}
				isTicketDraggable={false}
				ticketProps={{
					withContextMenu: isOwnBoard,
					action: () => {
						if (!canArrange) return <Fragment />;
						return <TicketAssignmentActionButton assignmentId={assignment.id} />;
					},
				}}
			/>
		</Draggable>
	);
}
